import { useState, useCallback } from 'react';
import { Camera, Keyboard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { MLScanner } from './ml-scanner';

export interface ScannerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScan: (result: string) => void;
  title?: string;
  description?: string;
}

type ScanMode = 'camera' | 'manual';

const PREFERRED_SCAN_MODE_KEY = 'h3gear_preferred_scan_mode';

function getInitialScanMode(): ScanMode {
  if (typeof window === 'undefined') return 'camera';
  const stored = localStorage.getItem(PREFERRED_SCAN_MODE_KEY);
  if (stored === 'manual') return 'manual';
  return 'camera';
}

function savePreferredScanMode(mode: ScanMode): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PREFERRED_SCAN_MODE_KEY, mode);
}

export function ScannerModal({
  open,
  onOpenChange,
  onScan,
  title = 'Scan Barcode',
  description,
}: ScannerModalProps) {
  const [activeTab, setActiveTab] = useState<ScanMode>(() => getInitialScanMode());
  const [manualInput, setManualInput] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);

  const handleTabChange = useCallback((value: string) => {
    const mode = value as ScanMode;
    setActiveTab(mode);
    savePreferredScanMode(mode);
  }, []);

  const handleScan = useCallback((result: string) => {
    setScanError(null);
    onScan(result);
    onOpenChange(false);
  }, [onScan, onOpenChange]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) {
      onScan(manualInput.trim());
      onOpenChange(false);
      setManualInput('');
    }
  };

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setManualInput('');
      setScanError(null);
    }
    onOpenChange(nextOpen);
  }, [onOpenChange]);

  const handleClose = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const handleScanError = useCallback((error: string) => {
    setScanError(error);
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <div className="px-4 pt-2">
            <TabsList className="w-full">
              <TabsTrigger value="camera" className="flex-1 gap-1 text-xs sm:text-sm">
                <Camera className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Camera</span>
                <span className="sm:hidden">Scan</span>
              </TabsTrigger>
              <TabsTrigger value="manual" className="flex-1 gap-1 text-xs sm:text-sm">
                <Keyboard className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Manual</span>
                <span className="sm:hidden">Type</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="camera" className="mt-0 p-4 pt-2">
            {activeTab === 'camera' && (
              <>
                {scanError && (
                  <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                    {scanError}
                  </div>
                )}
                <MLScanner
                  onScan={handleScan}
                  onError={handleScanError}
                  onClose={handleClose}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Auto-detects QR, Data Matrix, and barcodes
                </p>
              </>
            )}
          </TabsContent>

          <TabsContent value="manual" className="mt-0 p-4 pt-2">
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="manual-serial">Serial Number / Barcode Value</Label>
                <Input
                  id="manual-serial"
                  placeholder="Enter serial number or barcode value"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  autoFocus
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Type or paste the serial number if camera scanning is not available
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  Submit
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
